<template>
  <v-container fill-height :style="{
    backgroundColor: colors.blue,
    margin: 0,
    minWidth: '100%',
    position: 'relative'
  }">
    <!-- Language Switcher - Top Right -->
    <div style="position: absolute; top: 20px; right: 20px; z-index: 10;">
      <LanguageSwitcher />
    </div>

    <div :style="{
      backgroundImage: 'url(/assets/bg.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      opacity: 0.1
    }"></div>

    <v-row justify="center" align="center" class="fill-height">
      <v-col cols="12" sm="8" md="6" lg="4">
        <v-card class="pa-6" elevation="8">
          <v-card-title class="text-center mb-4">
            <h2>{{ $t('login.title') }}</h2>
          </v-card-title>

          <!-- Login Form -->
          <v-form v-if="!showForgotPassword" ref="loginForm" @submit.prevent="login">
            <v-text-field
              v-model="email"
              :label="$t('login.email')"
              type="email"
              variant="outlined"
              prepend-inner-icon="mdi-email"
              :rules="emailRules"
              :error-messages="emailErrors"
              required
              @input="clearEmailError"
            ></v-text-field>

            <v-text-field
              v-model="password"
              :label="$t('login.password')"
              :type="showPassword ? 'text' : 'password'"
              variant="outlined"
              prepend-inner-icon="mdi-lock"
              :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append-inner="showPassword = !showPassword"
              :rules="passwordRules"
              :error-messages="passwordErrors"
              required
              @input="clearPasswordError"
            ></v-text-field>

            <v-alert v-if="errorMessage" type="error" class="mb-4" closable @click:close="errorMessage = ''">
              {{ errorMessage }}
            </v-alert>

            <v-btn
              type="submit"
              :color="colors.primary"
              block
              size="large"
              :loading="loading"
              :disabled="loading"
              class="mb-3"
            >
              {{ $t('login.signIn') }}
            </v-btn>

            <v-btn
              text
              block
              @click="showForgotPassword = true"
              :color="colors.primary"
            >
              {{ $t('login.forgotPassword') }}
            </v-btn>

            <!-- <v-divider class="my-4"></v-divider>

            <v-btn
              text
              block
              @click="goToResults"
              color="grey"
            >
              Voir les résultats
            </v-btn> -->
          </v-form>

          <!-- Forgot Password Form -->
          <v-form v-else ref="forgotPasswordForm" @submit.prevent="forgotPassword">
            <v-card-subtitle class="text-center mb-4">
              {{ $t('forgotPassword.subtitle') }}
            </v-card-subtitle>

            <v-text-field
              v-model="forgotEmail"
              :label="$t('forgotPassword.email')"
              type="email"
              variant="outlined"
              prepend-inner-icon="mdi-email"
              :rules="emailRules"
              :error-messages="forgotEmailErrors"
              required
              @input="clearForgotEmailError"
            ></v-text-field>

            <v-alert v-if="forgotSuccessMessage" type="success" class="mb-4" closable @click:close="forgotSuccessMessage = ''">
              {{ forgotSuccessMessage }}
            </v-alert>

            <v-alert v-if="forgotErrorMessage" type="error" class="mb-4" closable @click:close="forgotErrorMessage = ''">
              {{ forgotErrorMessage }}
            </v-alert>

            <v-btn
              type="submit"
              :color="colors.primary"
              block
              size="large"
              :loading="forgotLoading"
              :disabled="forgotLoading"
              class="mb-3"
            >
              {{ $t('forgotPassword.sendLink') }}
            </v-btn>

            <v-btn
              text
              block
              @click="backToLogin"
              color="grey"
            >
              {{ $t('forgotPassword.backToLogin') }}
            </v-btn>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';
import axios from 'axios';
import api from '../api';
import colors from '../styles/colors';
import LanguageSwitcher from './LanguageSwitcher.vue';

export default {
  name: 'JuryLogin',
  components: {
    LanguageSwitcher,
  },
  data() {
    return {
      colors,
      email: '',
      password: '',
      showPassword: false,
      loading: false,
      errorMessage: '',
      emailErrors: [],
      passwordErrors: [],

      // Forgot password
      showForgotPassword: false,
      forgotEmail: '',
      forgotLoading: false,
      forgotSuccessMessage: '',
      forgotErrorMessage: '',
      forgotEmailErrors: [],
    };
  },
  computed: {
    // Validation rules
    emailRules() {
      return [
        v => !!v || this.$t('login.emailRequired'),
        v => /.+@.+\..+/.test(v) || this.$t('login.emailInvalid'),
      ];
    },
    passwordRules() {
      return [
        v => !!v || this.$t('login.passwordRequired'),
        v => (v && v.length >= 8) || this.$t('login.passwordMinLength'),
      ];
    },
  },
  methods: {
    ...mapActions(['updateJury', 'updateJuryToken', 'updateJuryPermissions']),

    clearEmailError() {
      this.emailErrors = [];
      this.errorMessage = '';
    },

    clearPasswordError() {
      this.passwordErrors = [];
      this.errorMessage = '';
    },

    clearForgotEmailError() {
      this.forgotEmailErrors = [];
      this.forgotErrorMessage = '';
    },

    validateEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    async login() {
      // Reset errors
      this.errorMessage = '';
      this.emailErrors = [];
      this.passwordErrors = [];

      // Validate form using Vuetify's form validation
      const { valid } = await this.$refs.loginForm.validate();
      
      if (!valid) {
        return;
      }

      // Additional manual validation
      if (!this.validateEmail(this.email)) {
        this.emailErrors = [this.$t('forgotPassword.emailInvalid')];
        return;
      }

      if (this.password.length < 8) {
        this.passwordErrors = [this.$t('login.passwordMinLength')];
        return;
      }

      this.loading = true;

      try {
        const response = await axios.post(api.juryLogin, {
          email: this.email.trim(),
          password: this.password,
        });

        if (response.data.success) {
          const { jury, token, permissions } = response.data.data;
          await this.updateJuryToken(token);
          await this.updateJury(jury);
          await this.updateJuryPermissions(permissions || []);

          // Redirect based on permission level
          if (permissions && (permissions.includes('add_monthly_data') || permissions.includes('add_pitch_data'))) {
            this.$router.push('/evaluation');
          } else {
            this.$router.push('/results');
          }
        } else {
          this.errorMessage = response.data.message || this.$t('login.connectionError');
        }
      } catch (error) {
        console.error('Login error:', error);

        if (error.response) {
          // Server responded with error
          if (error.response.status === 401) {
            this.errorMessage = this.$t('login.invalidCredentials');
          } else if (error.response.status === 422) {
            // Validation errors from backend
            const errors = error.response.data.errors;
            if (errors) {
              if (errors.email) {
                this.emailErrors = Array.isArray(errors.email) ? errors.email : [errors.email];
              }
              if (errors.password) {
                this.passwordErrors = Array.isArray(errors.password) ? errors.password : [errors.password];
              }
            }
            this.errorMessage = error.response.data.message || this.$t('login.validationError');
          } else {
            this.errorMessage = error.response.data.message || this.$t('login.connectionError');
          }
        } else if (error.request) {
          // Request made but no response
          this.errorMessage = this.$t('login.serverError');
        } else {
          // Something else happened
          this.errorMessage = this.$t('login.unexpectedError');
        }
      } finally {
        this.loading = false;
      }
    },

    async forgotPassword() {
      // Reset messages
      this.forgotSuccessMessage = '';
      this.forgotErrorMessage = '';
      this.forgotEmailErrors = [];

      // Validate form
      const { valid } = await this.$refs.forgotPasswordForm.validate();
      
      if (!valid) {
        return;
      }

      // Additional validation
      if (!this.validateEmail(this.forgotEmail)) {
        this.forgotEmailErrors = [this.$t('forgotPassword.emailInvalid')];
        return;
      }

      this.forgotLoading = true;

      try {
        const response = await axios.post(api.juryForgotPassword, {
          email: this.forgotEmail.trim(),
        });

        if (response.data.success) {
          this.forgotSuccessMessage = response.data.message || this.$t('forgotPassword.successMessage');
          // Clear the email field after successful submission
          this.forgotEmail = '';
          this.$refs.forgotPasswordForm.reset();
        }
      } catch (error) {
        console.error('Forgot password error:', error);

        if (error.response) {
          if (error.response.status === 422) {
            const errors = error.response.data.errors;
            if (errors && errors.email) {
              this.forgotEmailErrors = Array.isArray(errors.email) ? errors.email : [errors.email];
            }
            this.forgotErrorMessage = error.response.data.message || this.$t('forgotPassword.validationError');
          } else if (error.response.status === 404) {
            this.forgotErrorMessage = this.$t('forgotPassword.noAccountFound');
          } else {
            this.forgotErrorMessage = error.response.data.message || this.$t('forgotPassword.validationError');
          }
        } else if (error.request) {
          this.forgotErrorMessage = this.$t('forgotPassword.serverError');
        } else {
          this.forgotErrorMessage = this.$t('forgotPassword.unexpectedError');
        }
      } finally {
        this.forgotLoading = false;
      }
    },

    backToLogin() {
      this.showForgotPassword = false;
      this.forgotEmail = '';
      this.forgotSuccessMessage = '';
      this.forgotErrorMessage = '';
      this.forgotEmailErrors = [];
      if (this.$refs.forgotPasswordForm) {
        this.$refs.forgotPasswordForm.reset();
      }
    },

    goToResults() {
      this.$router.push('/results');
    },
  },
};
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
}
</style>